import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageSquare, ArrowLeft, ExternalLink } from "lucide-react";

export default function Messages() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();

  const loadConvs = () => api.get("/conversations").then((r) => setConvs(r.data));
  useEffect(() => { loadConvs(); }, []);

  const loadMessages = (convId) => api.get(`/conversations/${convId}/messages`).then((r) => { setActive(r.data.conversation); setMessages(r.data.messages); });
  useEffect(() => { if (id) loadMessages(id); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // poll for new messages in active conversation
  useEffect(() => {
    if (!id) return;
    const t = setInterval(() => loadMessages(id), 5000);
    return () => clearInterval(t);
  }, [id]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const r = await api.post(`/conversations/${active.id}/messages`, { text });
    setMessages((m) => [...m, r.data]);
    setText("");
    loadConvs();
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-900 text-2xl uppercase tracking-tight mb-4">Messages</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        {/* List */}
        <div className={`md:col-span-1 bg-[#141414] border border-border rounded-sm overflow-y-auto ${id ? "hidden md:block" : ""}`}>
          {convs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm"><MessageSquare className="w-8 h-8 mx-auto mb-2" />No conversations yet.</div>
          ) : convs.map((c) => (
            <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => navigate(`/messages/${c.id}`)} className={`w-full text-left p-4 border-b border-border hover:bg-secondary transition-colors ${active?.id === c.id ? "bg-secondary border-l-2 border-l-blaze" : ""}`}>
              <div className="flex items-center gap-2">
                <Avatar className="w-9 h-9 border border-border"><AvatarFallback className="bg-[#0A0A0A] text-xs">{c.other_name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-800 text-sm truncate">{c.other_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.last_message || "No messages yet"}</div>
                </div>
              </div>
              <div className="text-[11px] text-blaze mt-1.5 truncate spec-block">{c.listing_title}</div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className={`md:col-span-2 bg-[#141414] border border-border rounded-sm flex flex-col ${!id ? "hidden md:flex" : ""}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <button className="md:hidden" onClick={() => navigate("/messages")}><ArrowLeft className="w-5 h-5" /></button>
                <div className="flex-1">
                  <div className="font-heading font-800 text-sm">{active.buyer_id === user?.user_id ? active.seller_name : active.buyer_name}</div>
                  <Link to={`/listing/${active.listing_id}`} className="text-[11px] text-blaze flex items-center gap-1 spec-block">{active.listing_title} <ExternalLink className="w-3 h-3" /></Link>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-sm text-sm ${mine ? "bg-blaze text-white" : "bg-[#0A0A0A] border border-border"}`}>
                        {m.text}
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="bg-[#0A0A0A] border-border rounded-sm" />
                <Button data-testid="message-send-btn" onClick={send} className="bg-blaze hover:bg-blaze-hover rounded-sm"><Send className="w-4 h-4" /></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
